import { File as DBFile, EntityLabel } from '@esg/esg-global-types';
import { apollo_client } from '../app/db_util';
import {
  GET_DUPLICATE_FILE_FOR_METRIC_RECORD_QUERY,
  GET_FILES_FOR_METRIC_RECORDS_QUERY
} from '../graphql/queries/metric_capture/file';
import {
  CREATE_FILE_FOR_METRIC_RECORD_MUTATOR,
  DELETE_FILE_FOR_METRIC_RECORD_MUTATOR
} from '../graphql/mutations/metric_capture/file';
import { auth } from '../google/firebase';
import { generateUuidV4 } from '../../util/uuid';
import { MetadataError } from '@ep/error-handling';

// Singular and plural label for model entity.
export const file_label: EntityLabel = {
  one: 'File',
  many: 'Files'
};

/**
 * Function to query all file documents for current portal company and metric record.
 * @param {Group} group Group object of current portal company.
 * @param {string} company_id id of current portal company to reference firestore documents.
 * @param {string | undefined} metric_record_id Optionally specify a metric record to get all file documents for.
 * @returns {Promise<Array<DBFile>>}
 */
export const getFilesForMetricRecords = async (
  group_id: string,
  company_id: string,
  metric_record_id?: string
): Promise<Array<DBFile>> => {
  try {
    const files: Array<DBFile> = await apollo_client
      .query({
        variables: {
          group_id: group_id,
          company_id: company_id,
          metric_record_id: metric_record_id
        },
        query: GET_FILES_FOR_METRIC_RECORDS_QUERY,
        fetchPolicy: 'no-cache'
      })
      .then((response) => {
        return response.data.files;
      });

    return files;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/file.ts failed on an unknown error while calling getFilesForMetricRecords.',
      {
        group_id: group_id,
        company_id: company_id,
        metric_record_id: metric_record_id
      },
      tracking_id
    );
  }
};

/**
 * Upload a metric record file to Cloud Storage and create the metadata document in firestore on successful upload.
 * @param {string} group_id ID of Group to create metric record file for.
 * @param {string} company_id ID of Company to create metric record file for.
 * @param {File} file File that was uploaded by the user.
 * @param {string} path Path of metric record file from root of cloud storage bucket.
 * @param {string} metric_record_id ID of metric record relating to file to be used for reference document.
 * @returns {Promise<DocumentReference>}
 */
export const createFileForMetricRecord = async (
  group_id: string,
  company_id: string,
  file_name: string,
  metric_record_id: string,
  file_path: string
): Promise<DBFile> => {
  try {
    const new_file: DBFile = await apollo_client
      .mutate({
        mutation: CREATE_FILE_FOR_METRIC_RECORD_MUTATOR,
        variables: {
          group_id: group_id,
          company_id: company_id,
          file_name: file_name,
          metric_record_id: metric_record_id,
          file_path: file_path,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => {
        return response.data.createFile;
      });
    return new_file;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/file.ts failed on an unknown error while calling createFileForMetricRecord.',
      {
        group_id: group_id,
        company_id: company_id,
        metric_record_id: metric_record_id,
        file_name: file_name,
        file_path: file_path
      },
      tracking_id
    );
  }
};

/**
 * Soft delete relative metric record file record on firestore.
 * @param {string} group_id ID of Group to delete metric record file for.
 * @param {string} company_id ID of Company to delete metric record file for.
 * @param {DBFile} file Metric record file to delete.
 * @returns {Promise<void>}
 */
export const deleteFileForMetricRecord = async (
  group_id: string,
  company_id: string,
  file: DBFile
): Promise<void> => {
  try {
    await apollo_client
      .mutate({
        mutation: DELETE_FILE_FOR_METRIC_RECORD_MUTATOR,
        variables: {
          group_id: group_id,
          company_id: company_id,
          file: file,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => {
        return response.data.deleteFile;
      });
    return;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/file.ts failed on an unknown error while calling deleteFileForMetricRecord.',
      {
        group_id: group_id,
        company_id: company_id,
        file: file
      },
      tracking_id
    );
  }
};

/**
 * Return duplicate metric record file data of an uploaded file name.
 * @param {string} group_id ID of Group containing metric record files collection.
 * @param {string} company_id ID of company to check duplication against.
 * @param {string} file_name Metric record file name to check.
 * @param {string} metric_record_id ID of metric record to check duplication against.
 * @returns {Promise<DBFile | null>}
 */
export const getDuplicateFileForMetricRecord = async (
  group_id: string,
  company_id: string,
  file_name: string,
  metric_record_id: string
): Promise<DBFile | null> => {
  try {
    return await apollo_client
      .query({
        variables: {
          group_id: group_id,
          company_id: company_id,
          file_name: file_name,
          metric_record_id: metric_record_id
        },
        query: GET_DUPLICATE_FILE_FOR_METRIC_RECORD_QUERY,
        fetchPolicy: 'no-cache'
      })
      .then((response) => {
        return response.data.getDuplicateFile;
      });
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/file.ts failed on an unknown error while calling getDuplicateFileForMetricRecord.',
      {
        group_id: group_id,
        company_id: company_id,
        file_name: file_name,
        metric_record_id: metric_record_id
      },
      tracking_id
    );
  }
};
